import { MAX_CHARACTER_LEVEL, experienceForLevel, experienceToNextLevel, goldCapacity, levelDisagreement } from '@d2/core';
import { type CharacterSummary } from '@d2/schema/characters';

import { type TownCharacterView } from './town-screen-model';

/**
 * A roster row, as the town screen wants it — **composed out of `@d2/core`**.
 *
 * The screen draws numbers and produces none of them (`town-screen.tsx`), which
 * makes this the module that has to be right: it is the caller the screen's
 * contract keeps talking about. It is the same job `character-view.ts` does for
 * the HUD and the skill tree, and it is held to the same rule — every rule is a
 * **call**, never a comparison written here.
 *
 * There is one rule in it today, and it is `goldCapacity(level)`. A purse is
 * measured against a cap that is a function of level, core owns that function,
 * and a `level * GOLD_PER_LEVEL` written in this package would be the same rule
 * in two places with the second copy going stale (`CLAUDE.md`). Nothing else in
 * a `CharacterSummary` needs deciding: a name, a class, a level and a gold
 * balance are facts the API stated, and they are carried through untouched.
 *
 * ## Experience arrived on 2026-08-13, and the bar with it
 *
 * `CharacterSummary` now carries a lifetime `experience`, served from the
 * column the roster query already selected (`TODO.md` #481). So the two fields
 * the screen wants are the two calls this module always said they would be,
 * made out of core and nowhere else:
 *
 *     experienceIntoLevel: experience - experienceForLevel(level),
 *     experienceForNextLevel: experienceToNextLevel(level),
 *
 * What has *not* changed is the rule about not showing something untrue. Three
 * cases still answer **absent** rather than a number:
 *
 * - a level core's tables cannot describe, for the reason below;
 * - a level and an experience that **cannot both be true**. The roster reports
 *   an impossible pair verbatim by design — refusing on a read would hide a
 *   whole roster behind one bad row — and it is reachable through the ordinary
 *   write path, because a save carrying no progression is deliberately not held
 *   to the stored total. Drawing it would mean a negative bar or one past its
 *   own end. `levelDisagreement` is core's own predicate for it, and asking it
 *   here is a call rather than a second opinion.
 * - maximum level, where `experienceToNextLevel` is 0 — which the screen reads
 *   as *max level* rather than as a missing answer, and draws accordingly.
 *
 * A level is still never a substitute for an experience: `experienceForLevel(12)`
 * is the *floor* of level 12, so quoting it back would draw every character at
 * exactly 0% of their level dressed up as a real number. You can compute the
 * level from the experience and never the other way round.
 */

/**
 * A level core's tables will answer for.
 *
 * The same predicate `character-view.ts` guards the HUD with, and for the same
 * reason: `goldCapacity` **throws** a `RangeError` for anything that is not a
 * whole number of at least one — rightly, inside the simulation, where such a
 * level is a programmer error. Out here the level arrived over a network, and an
 * exception thrown while rendering town is not a caught error, it is a blank
 * page. So a level the table cannot describe is an ordinary refusal
 * (`CLAUDE.md`), and what it returns is a cap of zero — a state the screen
 * already draws without dividing by it.
 */
const isDescribedLevel = (level: number): boolean =>
  Number.isInteger(level) && level >= 1 && level <= MAX_CHARACTER_LEVEL;

/**
 * What town shows about a character, given the row the roster has.
 *
 * Every field is either the API's own or core's; none of them is this file's.
 */
export const townViewOf = (character: CharacterSummary): TownCharacterView => {
  const { level, experience } = character;
  const described = isDescribedLevel(level);
  // Both fields travel together or neither does: half an answer is a bar drawn
  // against a span nobody stated.
  const banked = described && levelDisagreement({ level, experience }) === null;

  return {
    name: character.name,
    characterClass: character.characterClass,
    level,
    gold: character.gold,
    goldCapacity: described ? goldCapacity(level) : 0,
    ...(banked
      ? {
          experienceIntoLevel: experience - experienceForLevel(level),
          experienceForNextLevel: experienceToNextLevel(level),
        }
      : {}),
  };
};
